// Centralized photo asset list, drop new files into /public/photos/ and add them here.
// Photo identifications (verified by content analysis):
// hh-01, hh-02 = River Valley Church renderings (commercial)
// hh-03, hh-09, hh-16, hh-20 = Exterior finished residential builds
// hh-12 = Exterior aerial (with patio)
// hh-04, hh-17, hh-18, hh-22, hh-23 = Finished kitchens
// hh-11 = Kitchen mid-renovation (light green cabinets)
// hh-05, hh-08, hh-13, hh-21 = Living spaces (hh-13/hh-21 = beams + fireplace)
// hh-07, hh-10, hh-19, hh-24 = Bathrooms
// hh-06 = Walk-in closet
// hh-14, hh-15, hh-25 = Detail carpentry / built-ins / cabinetry
// All photos converted to optimized WebP (≤1600px, ≤200KB each) — Oct 2026 performance pass.

// Intrinsic dimensions (post-conversion) for width/height attributes — prevents layout shift (CLS)
export const PHOTO_DIMS = {
 "hh-01": { w: 1600, h: 900 }, "hh-02": { w: 1600, h: 900 }, "hh-03": { w: 1600, h: 1200 },
 "hh-04": { w: 1600, h: 1200 }, "hh-05": { w: 1600, h: 1200 }, "hh-06": { w: 1536, h: 2048 },
 "hh-07": { w: 1536, h: 2048 }, "hh-08": { w: 1536, h: 2048 }, "hh-09": { w: 1600, h: 1200 },
 "hh-10": { w: 1536, h: 2048 }, "hh-11": { w: 1600, h: 1200 }, "hh-12": { w: 1280, h: 960 },
 "hh-13": { w: 1536, h: 2048 }, "hh-14": { w: 1536, h: 2048 }, "hh-15": { w: 1536, h: 2048 },
 "hh-16": { w: 1152, h: 1536 }, "hh-17": { w: 1600, h: 1200 }, "hh-18": { w: 1536, h: 2048 },
 "hh-19": { w: 1536, h: 2048 }, "hh-20": { w: 1280, h: 960 }, "hh-21": { w: 1600, h: 1200 },
 "hh-22": { w: 1600, h: 1200 }, "hh-23": { w: 1600, h: 1200 }, "hh-24": { w: 1440, h: 1920 },
 "hh-25": { w: 1440, h: 1920 }, "hh-26": { w: 1600, h: 2133 }, "hh-27": { w: 1600, h: 2133 },
 "hh-28": { w: 1600, h: 1200 }, "hh-29": { w: 1600, h: 1200 }, "hh-30": { w: 1600, h: 2133 },
 "hh-31": { w: 1600, h: 2133 }, "hh-32": { w: 1600, h: 2133 }, "hh-33": { w: 1600, h: 2133 },
 "hh-34": { w: 1600, h: 2133 }, "hh-35": { w: 1600, h: 2133 }, "hh-36": { w: 1600, h: 2133 },
 "hh-37": { w: 1600, h: 1200 }, "hh-38": { w: 1152, h: 1535 }, "hh-39": { w: 1280, h: 960 },
 "hh-40": { w: 1600, h: 2133 }, "hh-41": { w: 1600, h: 1200 }, "hh-42": { w: 1600, h: 2133 },
 "hh-43": { w: 1600, h: 2133 }, "hh-44": { w: 1600, h: 2133 }, "hh-45": { w: 1600, h: 2133 },
 "hh-46": { w: 1280, h: 960 }, "hh-47": { w: 1600, h: 1200 }, "hh-48": { w: 1600, h: 2133 },
 "hh-49": { w: 1600, h: 1200 }, "hh-50": { w: 1600, h: 2133 }, "hh-51": { w: 1600, h: 2133 },
 "hh-52": { w: 1600, h: 2133 }, "hh-53": { w: 1600, h: 1200 }, "hh-54": { w: 1600, h: 2133 },
 "hh-55": { w: 1600, h: 1200 }, "hh-56": { w: 1600, h: 1200 }, "hh-57": { w: 1280, h: 960 },
 "hh-58": { w: 1152, h: 1535 },
};

// Intrinsic { w, h } for a "/photos/hh-XX.webp" path, or undefined if unknown
export function photoDims(url) {
 const m = /hh-(\d+)/.exec(url || "");
 return m ? PHOTO_DIMS[`hh-${m[1]}`] : undefined;
}

// HERO, strongest finished exterior
export const HERO_PHOTO = "/photos/hh-03.webp";

// "A home isn't a product, it's where your life happens" → open-plan living with beams
export const HOME_INTRO_PHOTO = "/photos/hh-21.webp";

// "Why Harris Homes / Built by hand" → craftsmanship close-up
export const CRAFTSMANSHIP_PHOTO = "/photos/hh-15.webp";

// About page side photo → established residential exterior with lawn
export const ABOUT_PHOTO = "/photos/hh-16.webp";

// Per-service imagery, each handpicked to match the service
export const SERVICE_PHOTOS = {
 "custom-homes": "/photos/hh-03.webp", // finished modern farmhouse
 "remodels": "/photos/hh-11.webp", // kitchen mid-renovation
 "additions": "/photos/hh-12.webp", // aerial showing whole-home + patio addition
 "residential-construction": "/photos/hh-20.webp",// finished ground-up build
 "project-management": "/photos/hh-08.webp", // completed living space (result of good PM)
 "craftsmanship": "/photos/hh-15.webp", // built-in pantry / detail carpentry
};

// Curated home featured-work strip (6 tiles, mix of exterior + interior + detail)
export const FEATURED_PHOTOS = [
 "/photos/hh-03.webp", // hero exterior (large)
 "/photos/hh-21.webp", // living
 "/photos/hh-17.webp", // kitchen
 "/photos/hh-19.webp", // bath
 "/photos/hh-15.webp", // detail
 "/photos/hh-09.webp", // second exterior
];

// About page photo strip, exterior, living, kitchen, detail
export const ABOUT_STRIP_PHOTOS = [
 "/photos/hh-03.webp",
 "/photos/hh-21.webp",
 "/photos/hh-17.webp",
 "/photos/hh-15.webp",
];

// All photos for the Projects page masonry, exterior first, then interiors, then details, then renderings last
export const ALL_PHOTOS = [
 "/photos/hh-03.webp",
 "/photos/hh-09.webp",
 "/photos/hh-21.webp",
 "/photos/hh-20.webp",
 "/photos/hh-17.webp",
 "/photos/hh-12.webp",
 "/photos/hh-13.webp",
 "/photos/hh-04.webp",
 "/photos/hh-16.webp",
 "/photos/hh-22.webp",
 "/photos/hh-08.webp",
 "/photos/hh-18.webp",
 "/photos/hh-23.webp",
 "/photos/hh-05.webp",
 "/photos/hh-19.webp",
 "/photos/hh-15.webp",
 "/photos/hh-07.webp",
 "/photos/hh-24.webp",
 "/photos/hh-10.webp",
 "/photos/hh-14.webp",
 "/photos/hh-25.webp",
 "/photos/hh-06.webp",
 "/photos/hh-11.webp",
 "/photos/hh-01.webp",
 "/photos/hh-02.webp",
];
